import type { CardPricePoint } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { cardTitle, formatCents } from "../../core/primitives/card-display";
import { formatWhen } from "../../core/primitives/format";
import { PriceHistoryActions, PriceHistoryState } from "./price-history-model";

/**
 * One question, one answer: where this price came from.
 *
 * These are the current names. The column used to be MARKET / MARKED / FLOOR,
 * which answered three different questions and only two of them about the
 * asking price — see `enum CardPriceKind` in the schema.
 */
const KIND_LABEL: Record<CardPricePoint["kind"], string> = {
  ESTIMATED: "Estimated",
  SET: "Asking",
  REPRICED: "Repriced",
};

/** Percent change between the first and latest market observation. */
function TrendBadge({ market }: { market: readonly CardPricePoint[] }) {
  if (market.length < 2) return null;
  const first = market[0].priceCents;
  const last = market[market.length - 1].priceCents;
  if (!first) return null;

  const change = ((last - first) / first) * 100;
  if (Math.abs(change) < 0.05) return null;
  const up = change > 0;
  return (
    // A rise is good news for a shop holding stock, so it takes the accent; a
    // fall takes the danger red. This is the one place the two are read as a
    // pair rather than as "ready" and "broken".
    <span className={`badge ${up ? "badge-accent" : "badge-danger"}`}>
      {up ? "▲" : "▼"} {Math.abs(change).toFixed(1)}%
    </span>
  );
}

/** The recorded price history for one card. Renders nothing while no card is open. */
export function PriceHistoryModal() {
  const state = useSubtree(PriceHistoryState);
  const actions = useSubtree(PriceHistoryActions);
  const observer = useObserver();

  const card = observer.watch(state.card);
  if (!card) return null;
  const status = observer.watch(state.status);
  const prices = observer.watch(state.prices);

  // The pipeline's readings, as opposed to a price a person typed: an estimate
  // and every fresh lookup that replaced one.
  const market = prices.filter((p) => p.kind === "ESTIMATED" || p.kind === "REPRICED");
  // Newest first reads better in a list, though the API returns oldest first.
  const entries = [...prices].reverse();
  const first = market[0];

  return (
    <div className="scrim items-center justify-center p-4">
      <div className="modal w-full max-w-md p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-white">Price history</h2>
            <p className="truncate text-xs text-muted">{cardTitle(card)}</p>
          </div>
          <button type="button" onClick={actions.close} className="btn-chip shrink-0">
            Close
          </button>
        </div>

        {status.kind === "loading" || status.kind === "idle" ? (
          <p className="py-4 text-sm text-muted">Loading…</p>
        ) : status.kind === "error" ? (
          <p className="py-4 text-sm text-danger">Failed to load price history.</p>
        ) : entries.length === 0 ? (
          <p className="py-4 text-sm text-muted">
            No prices recorded yet. Use “Refresh price” to fetch the current market price.
          </p>
        ) : (
          <>
            {first && (
              <div className="mb-3 flex items-center gap-2 text-xs text-muted">
                <span>
                  First {formatCents(first.priceCents)} on {formatWhen(first.recordedAt)}
                </span>
                <TrendBadge market={market} />
              </div>
            )}
            <ul className="max-h-72 divide-y divide-line overflow-y-auto">
              {entries.map((entry, i) => (
                <li key={`${entry.kind}-${entry.recordedAt}`} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0 text-xs text-muted">
                    <span className="text-faint">{KIND_LABEL[entry.kind]}</span> · {formatWhen(entry.recordedAt)}
                  </span>
                  {/* Only the latest reading is a live price; the rest are history. */}
                  <span className={`shrink-0 text-sm font-semibold ${i === 0 ? "text-accent" : "text-muted"}`}>
                    {formatCents(entry.priceCents)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[10px] text-faint">
              Market readings are the raw (ungraded) price at each lookup; asking and floor prices are what the shop
              set.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
